'use client';

import Image from 'next/image';

import { ChevronDown, ExternalLink, Github, Linkedin } from 'lucide-react';

import { useConsent } from '@/components/consent/consent-context';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TranslationInterface } from '@/lib/i18n';
import { useLocale, useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

type linkType = 'DROPDOWN' | 'LINK' | 'EXTERNAL_LINK';

interface NavigationLink {
  type?: linkType;
  nameKey: keyof TranslationInterface['footer'];
  href?: string;
  /** Render without a locale prefix — for routes that exist in English only. */
  unlocalized?: boolean;
  /**
   * Show only on /ar. For customers the Arabic site leads with and the
   * English site does not list — the story page stays reachable in both
   * locales, it just is not advertised here.
   */
  arOnly?: boolean;
  links?: Omit<NavigationLink, 'type'>[];
}

interface Navigation {
  titleKey: keyof TranslationInterface['footer'];
  links: NavigationLink[];
}

const NAVIGATION: Array<Navigation> = [
  {
    titleKey: 'Helpdesks',
    links: [
      { type: 'LINK', nameKey: 'Zendesk', href: '/helpdesks/zendesk' },
      { type: 'LINK', nameKey: 'Salesforce', href: '/helpdesks/salesforce' },
      { type: 'LINK', nameKey: 'Intercom', href: '/helpdesks/intercom' },
      { type: 'LINK', nameKey: 'HubSpot', href: '/helpdesks/hubspot' },
      { type: 'LINK', nameKey: 'Freshdesk', href: '/helpdesks/freshdesk' },
      { type: 'LINK', nameKey: 'Twilio Flex', href: '/helpdesks/twilio-flex' },
      { type: 'LINK', nameKey: 'Open Helpdesk', href: '/helpdesks/open' },
      { type: 'LINK', nameKey: 'All Helpdesks', href: '/helpdesks' },
    ],
  },
  {
    titleKey: 'Product',
    links: [
      { type: 'LINK', nameKey: 'Agent 5', href: '/agent5' },
      { type: 'LINK', nameKey: 'Open Telephony', href: '/open-telephony' },
      { type: 'LINK', nameKey: 'Workflows', href: '/workflows' },
      { type: 'LINK', nameKey: 'Integrations', href: '/integrations' },
      {
        type: 'LINK',
        nameKey: 'Customer Insights',
        href: '/customer-insights',
      },
      {
        type: 'LINK',
        nameKey: 'Knowledge Gap Detection',
        href: '/knowledge-gap-detection',
      },
      { type: 'LINK', nameKey: 'App Builder', href: '/app-builder' },
      {
        type: 'LINK',
        nameKey: 'Outbound Sequencing',
        href: '/outbound-sequencing',
      },
      { type: 'LINK', nameKey: 'Templates', href: '/templates' },
      { type: 'LINK', nameKey: 'Use Cases', href: '/use-cases' },
      { type: 'LINK', nameKey: 'Companion', href: '/companion' },
      { type: 'LINK', nameKey: 'White-label', href: '/whitelabel' },
      {
        type: 'LINK',
        nameKey: 'Compare Platforms',
        href: '/compare',
        unlocalized: true,
      },
      { type: 'LINK', nameKey: 'Pricing', href: '/pricing' },
    ],
  },
  // The footer is the only site-wide navigation that reaches the server-
  // rendered HTML — the navbar mega-menu mounts client-side, so a link that
  // lives only there is invisible to a crawler. Before this column, /voice and
  // /telephony had one inbound link between them (from /channels/voice), with
  // 188 pages behind it, and /compare had none at all.
  {
    titleKey: 'Voice Agents',
    links: [
      {
        type: 'LINK',
        nameKey: 'AI Receptionist',
        href: '/voice/ai-receptionist',
      },
      {
        type: 'LINK',
        nameKey: 'AI Answering Service',
        href: '/voice/ai-answering-service',
      },
      {
        type: 'LINK',
        nameKey: 'AI Call Center',
        href: '/voice/ai-call-center',
      },
      {
        type: 'LINK',
        nameKey: 'AI Voice Agent',
        href: '/voice/ai-voice-agent',
      },
      {
        type: 'LINK',
        nameKey: 'AI Outbound Calling',
        href: '/voice/ai-outbound-calling',
      },
      { type: 'LINK', nameKey: 'All Voice Use Cases', href: '/voice' },
      { type: 'LINK', nameKey: 'Telephony Providers', href: '/telephony' },
    ],
  },
  {
    titleKey: 'Customers',
    links: [
      { type: 'LINK', nameKey: 'Mollie', href: '/customer-stories/mollie' },
      {
        type: 'LINK',
        nameKey: 'TicketSwap',
        href: '/customer-stories/ticketswap',
      },
      {
        type: 'LINK',
        nameKey: 'MoneyGram',
        href: '/customer-stories/moneygram',
      },
      { type: 'LINK', nameKey: 'Trunkrs', href: '/customer-stories/trunkrs' },
      { type: 'LINK', nameKey: 'More.com', href: '/customer-stories/more' },
      {
        type: 'LINK',
        nameKey: 'Qoyod',
        href: '/customer-stories/qoyod',
        arOnly: true,
      },
      { type: 'LINK', nameKey: 'All Stories', href: '/customer-stories' },
    ],
  },
  {
    titleKey: 'Channels',
    links: [
      { type: 'LINK', nameKey: 'Voice AI', href: '/channels/voice' },
      { type: 'LINK', nameKey: 'Chat', href: '/channels/chat' },
      { type: 'LINK', nameKey: 'Email', href: '/channels/email' },
      { type: 'LINK', nameKey: 'WhatsApp', href: '/channels/whatsapp' },
      { type: 'LINK', nameKey: 'Slack', href: '/channels/slack' },
      { type: 'LINK', nameKey: 'Social Media', href: '/channels/social' },
    ],
  },
  {
    titleKey: 'Industries',
    links: [
      {
        type: 'LINK',
        nameKey: 'Financial Services',
        href: '/industries/financial-services',
      },
      { type: 'LINK', nameKey: 'Healthcare', href: '/industries/healthcare' },
      { type: 'LINK', nameKey: 'Mobility', href: '/industries/mobility' },
      { type: 'LINK', nameKey: 'Technology', href: '/industries/technology' },
      {
        type: 'LINK',
        nameKey: 'Travel & Hospitality',
        href: '/industries/travel-hospitality',
      },
      { type: 'LINK', nameKey: 'All Industries', href: '/industries' },
    ],
  },
];

// Tools and legal links are consolidated into single index-page links
// (/tools and /legal) inside the Company column below.
const SECONDARY_NAVIGATION: Array<Navigation> = [
  {
    titleKey: 'Company',
    links: [
      { type: 'LINK', nameKey: 'About Us', href: '/about-open' },
      { type: 'LINK', nameKey: 'Careers', href: 'https://careers.open.cx' },
      { type: 'LINK', nameKey: 'Cheatsheet', href: '/cheatsheet' },
      { type: 'LINK', nameKey: 'Blog', href: '/blog' },
      {
        type: 'LINK',
        nameKey: 'News',
        href: '/news/announcing-our-new-funding-round',
      },
      { type: 'LINK', nameKey: 'Contact', href: '/demo' },
      { type: 'LINK', nameKey: 'Free Tools', href: '/tools' },
      { type: 'LINK', nameKey: 'Legal', href: '/legal' },
    ],
  },
];

const SOCIAL_LINKS = [
  {
    icon: Github,
    nameKey: 'Github' as keyof TranslationInterface['footer'],
    href: 'https://github.com/opencx-labs',
  },
  {
    icon: Linkedin,
    nameKey: 'LinkedIn' as keyof TranslationInterface['footer'],
    href: 'https://www.linkedin.com/company/openso/?viewAsMember=true',
  },
];

const HOME_LINK = '/';

interface FooterSectionTitleProps {
  children: React.ReactNode;
}

const FooterSectionTitle = ({ children }: FooterSectionTitleProps) => {
  return <h3 className="mb-4 text-xs uppercase">{children}</h3>;
};

interface GetLinkProps {
  link: NavigationLink;
  t: TranslationInterface;
}

const GetLink = ({ link, t }: GetLinkProps) => {
  const { type, href, nameKey, links } = link;
  const name = t.footer[nameKey];

  if (type == 'EXTERNAL_LINK') {
    return (
      <a
        href={href}
        className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center gap-0.5 text-sm leading-5 transition-colors"
      >
        <div>{name}</div>
        <ExternalLink className="size-3.5" />
      </a>
    );
  }

  if (type == 'DROPDOWN') {
    return (
      <>
        <div className="block md:hidden">
          <Drawer>
            <DrawerTrigger className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center gap-0.5 text-sm leading-5 transition-colors">
              {name}
              <ChevronDown className="size-3.5" />
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{name}</DrawerTitle>
                <div>
                  {links?.map((subLink) => (
                    <Link
                      href={subLink.href || '#'}
                      prefetch={true}
                      className="hover:bg-muted block cursor-pointer rounded-lg px-2 py-2.5 text-sm!"
                      key={`drawer-footer-1-${subLink.nameKey}`}
                    >
                      {t.footer[subLink.nameKey]}
                    </Link>
                  ))}
                </div>
              </DrawerHeader>
              <DrawerFooter>
                <DrawerClose asChild>
                  <Button variant="outline">{t.common.Close}</Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
        <div className="hidden md:block">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center gap-0.5 text-sm leading-5 transition-colors">
                {name}
                <ChevronDown className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              {links?.map((subLink) => (
                <DropdownMenuItem
                  className="cursor-pointer rounded-lg py-2.5 text-sm!"
                  asChild
                  key={`dropdown-footer-1-${subLink.nameKey}`}
                >
                  <Link href={subLink.href || '#'} prefetch={true}>
                    {t.footer[subLink.nameKey]}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </>
    );
  }

  // `unlocalized` routes that exist in English only. The default `Link`
  // prefixes the active locale, and /ar/compare is a 404 — /compare is
  // English-only in v1 — so a locale-aware link there would put a broken
  // destination in the footer of every Arabic page.
  if (link.unlocalized) {
    return (
      <a
        href={href || '#'}
        className="text-muted-foreground hover:text-foreground cursor-pointer text-sm leading-5 transition-colors"
      >
        {name}
      </a>
    );
  }

  return (
    <Link
      href={href || '#'}
      prefetch={true}
      className="text-muted-foreground hover:text-foreground cursor-pointer text-sm leading-5 transition-colors"
    >
      {name}
    </Link>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { openPreferences } = useConsent();

  return (
    <footer className="text-foreground bg-[#fafaf9] pt-20 pb-0">
      <div className="container px-4 md:px-6">
        {/* Top CTA Section */}
        <div className="mb-16 space-y-6 py-12">
          <h2 className="max-w-xl font-serif text-2xl text-balance md:text-3xl">
            {t.footer['One AI agent for the entire customer experience']}
          </h2>
          <div className="flex flex-wrap gap-4">
            <Button
              className="h-11 rounded-full px-6 text-sm font-medium"
              asChild
            >
              <Link
                href="/demo"
                prefetch={true}
                aria-label={t.common['Learn more about Open and book a demo']}
              >
                {t.footer['Get Started']}
              </Link>
            </Button>
          </div>
        </div>

        {/* Links Section - Main Navigation Row
         *
         * Rendered by mapping the groups, NOT by addressing them positionally.
         * This block used to hardcode NAVIGATION[1], [3], [2], [4] and [0],
         * with comments naming columns that no longer matched the indices.
         * Inserting the Voice Agents group at index 2 therefore pushed
         * Industries to index 5, which nothing rendered — seven industry links
         * disappeared from every page on the site and /industries became an
         * orphan. Adding a group must not be able to delete another one.
         */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 pb-10 sm:grid-cols-3 lg:grid-cols-6 lg:gap-x-10">
          {[...SECONDARY_NAVIGATION, ...NAVIGATION].map((group) => (
            <div key={group.titleKey}>
              <FooterSectionTitle>
                {t.footer[group.titleKey]}
              </FooterSectionTitle>
              <ul className="space-y-2.5">
                {group.links
                  .filter((link) => !link.arOnly || locale === 'ar')
                  .map((link, i) => (
                    <li key={i}>
                      <GetLink link={link} t={t} />
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Security & compliance */}
        <div className="mt-14 border-t border-neutral-200 pt-10">
          <div className="flex flex-col items-center gap-7 text-center">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 sm:gap-x-14">
              {[
                {
                  src: 'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/AICPA-SOC.svg',
                  alt: 'AICPA SOC 2',
                  label: 'SOC 2 Type II',
                },
                {
                  src: 'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/AICPA-SOC.svg',
                  alt: 'AICPA SOC 2',
                  label: 'SOC 2 Type I',
                },
                {
                  src: 'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/ISO-27001.svg',
                  alt: 'ISO 27001',
                  label: 'ISO 27001',
                },
                {
                  src: 'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/compliance/GDPR.svg',
                  alt: 'GDPR',
                  label: 'GDPR',
                },
                {
                  src: '/compliance/eu-ai-act.svg',
                  alt: 'EU AI Act',
                  label: 'EU AI Act',
                },
                {
                  src: '/compliance/dora.svg',
                  alt: 'DORA',
                  label: 'DORA',
                },
                {
                  src: '/compliance/cpra.svg',
                  alt: 'CPRA',
                  label: 'CPRA',
                },
                {
                  src: '/compliance/tcpa.svg',
                  alt: 'TCPA',
                  label: 'TCPA',
                },
              ].map((badge, i) => (
                <div key={i} className="flex flex-col items-center gap-2.5">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={badge.src}
                    alt={badge.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-14 w-auto object-contain opacity-45 grayscale transition-opacity duration-200 hover:opacity-75"
                  />
                  <span className="text-[10px] font-medium tracking-[0.14em] text-neutral-400 uppercase">
                    {badge.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-border mt-14 flex flex-col items-start justify-between gap-6 border-t pt-8 md:flex-row md:items-center">
          <div className="space-y-4">
            <Link
              href={HOME_LINK}
              prefetch={true}
              aria-label={locale === 'ar' ? 'Open — الرئيسية' : 'Open — Home'}
              className="block"
            >
              <Image
                src="/Logo Long Black (1).svg"
                alt="Open Logo"
                width={100}
                height={32}
                className="h-6 w-auto object-contain"
              />
            </Link>
            <p className="text-muted-foreground max-w-md text-xs">
              {
                t.footer[
                  "Open is backed by Y Combinator and some of the world's best investors. We're building the future of customer support."
                ]
              }
            </p>

            {/* Arabic Images - Only visible for Arabic locale */}
            {locale === 'ar' && (
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Image
                  src="/images/arabic-website/2030.png"
                  alt="Saudi Vision 2030"
                  width={60}
                  height={40}
                  className="h-8 w-auto object-contain opacity-70 grayscale transition-all hover:opacity-100 hover:grayscale-0"
                />
                <Image
                  src="/images/arabic-website/sbc-ar.png"
                  alt="SBC"
                  width={60}
                  height={40}
                  className="h-8 w-auto object-contain opacity-70 grayscale transition-all hover:opacity-100 hover:grayscale-0"
                />
                <Image
                  src="/images/arabic-website/cstc.png"
                  alt="CSTC"
                  width={60}
                  height={40}
                  className="h-8 w-auto object-contain opacity-70 grayscale transition-all hover:opacity-100 hover:grayscale-0"
                />
              </div>
            )}
          </div>

          <div className="text-muted-foreground flex flex-col items-start gap-2 text-xs md:items-end">
            <div className="flex items-center gap-4">
              {SOCIAL_LINKS.map((link, i) => (
                <a
                  key={i}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs transition-colors"
                >
                  <link.icon className="size-3.5" />
                  {t.footer[link.nameKey]}
                </a>
              ))}
            </div>
            <p>
              © {new Date().getFullYear()} Open.{' '}
              {t.footer['All rights reserved']}.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 md:justify-end">
              <Link
                href="/privacy-policy"
                className="hover:text-foreground transition-colors"
              >
                {t.demo['Privacy Policy']}
              </Link>
              <Link
                href="/cookie-policy"
                className="hover:text-foreground transition-colors"
              >
                {t.common['Cookie Policy']}
              </Link>
              {/* Reopens the consent preferences so visitors can change or
                  withdraw consent at any time (Art. 7(3) GDPR). */}
              <button
                onClick={openPreferences}
                className="hover:text-foreground cursor-pointer transition-colors"
              >
                {t.common['Cookie settings']}
              </button>
              {/* CPRA §1798.135 — same preferences panel; the marketing
                  toggle controls "sharing" with ad/identity partners. */}
              <button
                onClick={openPreferences}
                className="hover:text-foreground cursor-pointer transition-colors"
              >
                {t.common['Do Not Sell or Share My Personal Information']}
              </button>
            </div>
            <Button
              asChild
              variant="link"
              className="text-muted-foreground hover:text-foreground h-auto p-0 text-xs font-normal"
            >
              <a
                href="https://status.open.cx"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <div className="relative size-2">
                  <span className="absolute top-1/2 left-1/2 z-10 size-2 -translate-1/2 animate-pulse rounded-full bg-green-400/50" />
                  <span className="absolute top-1/2 left-1/2 z-20 size-1.5 -translate-1/2 rounded-full bg-green-500" />
                </div>
                {t.footer['All systems operational']}
              </a>
            </Button>
          </div>
        </div>
      </div>

      <div className="h-16" />
    </footer>
  );
};

export { Footer };
